import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CopyIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteIcon from '@mui/icons-material/DeleteOutlineRounded';
import { format, useTranslation } from '../i18n';
import { PageHeader } from '../components/Section';

type ApiKey = { id: string; name: string; prefix: string; created_at: string; last_used_at: string | null };
type CreatedKey = ApiKey & { key: string };

const NAME_MAX = 60;

export default function ApiKeys() {
  const t = useTranslation();
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [max, setMax] = useState(10);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [toRevoke, setToRevoke] = useState<ApiKey | null>(null);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/account/api-keys', { credentials: 'include' });
      if (!res.ok) throw new Error(String(res.status));
      const data = (await res.json()) as { items: ApiKey[]; max_active?: number };
      setKeys(data.items);
      if (data.max_active) setMax(data.max_active);
    } catch {
      setKeys([]);
      setError(t.apiKeys.errors.load);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setCreating(true);
    try {
      const res = await fetch('/api/v1/account/api-keys', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      const data = (await res.json().catch(() => ({}))) as Partial<CreatedKey> & { error?: string };
      if (!res.ok) {
        setError(
          data.error === 'limit_reached'
            ? format(t.apiKeys.errors.limit, { max })
            : res.status === 429
              ? t.apiKeys.errors.rateLimited
              : t.apiKeys.errors.create
        );
        return;
      }
      setCreated(data as CreatedKey);
      setCopied(false);
      setName('');
      await load();
    } catch {
      setError(t.apiKeys.errors.create);
    } finally {
      setCreating(false);
    }
  };

  const onCopy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.key);
      setCopied(true);
    } catch {
      /* the key is selectable in the dialog */
    }
  };

  const onRevoke = async () => {
    if (!toRevoke) return;
    setRevoking(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/account/api-keys/${toRevoke.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok && res.status !== 404) throw new Error(String(res.status));
      setToRevoke(null);
      await load();
    } catch {
      setError(t.apiKeys.errors.revoke);
    } finally {
      setRevoking(false);
    }
  };

  const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <Box>
      <PageHeader title={t.apiKeys.title} description={t.apiKeys.description} />
      <Stack spacing={3} sx={{ maxWidth: 720 }}>
        {error && <Alert severity="error">{error}</Alert>}

        <Paper sx={{ p: 3 }}>
          <Box component="form" onSubmit={onCreate}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
              <TextField
                label={t.apiKeys.nameLabel}
                placeholder={t.apiKeys.namePlaceholder}
                value={name}
                onChange={(e) => setName(e.target.value)}
                inputProps={{ maxLength: NAME_MAX }}
                required
                fullWidth
              />
              <Button type="submit" variant="contained" disabled={creating || !name.trim()} sx={{ flexShrink: 0, minWidth: 140 }}>
                {creating ? t.common.loading : t.apiKeys.create}
              </Button>
            </Stack>
          </Box>
        </Paper>

        <Paper sx={{ p: 0 }}>
          {keys === null ? (
            <Typography sx={{ p: 3 }} color="text.secondary">
              {t.common.loading}
            </Typography>
          ) : keys.length === 0 ? (
            <Typography sx={{ p: 3 }} color="text.secondary">
              {t.apiKeys.empty}
            </Typography>
          ) : (
            <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
              {keys.map((k) => (
                <Stack key={k.id} direction="row" alignItems="center" spacing={2} sx={{ px: 3, py: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600 }} noWrap>
                      {k.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {k.prefix}…
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {format(t.apiKeys.createdOn, { date: when(k.created_at) })} ·{' '}
                      {k.last_used_at ? format(t.apiKeys.lastUsed, { date: when(k.last_used_at) }) : t.apiKeys.neverUsed}
                    </Typography>
                  </Box>
                  <Tooltip title={t.apiKeys.revoke}>
                    <IconButton aria-label={`${t.apiKeys.revoke} ${k.name}`} onClick={() => setToRevoke(k)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          )}
        </Paper>
        {keys && keys.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {format(t.apiKeys.usage, { count: keys.length, max })}
          </Typography>
        )}
      </Stack>

      <Dialog open={!!created} maxWidth="sm" fullWidth onClose={(_e, reason) => reason !== 'backdropClick' && setCreated(null)}>
        <DialogTitle>{t.apiKeys.createdTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t.apiKeys.createdBody}</DialogContentText>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              value={created?.key ?? ''}
              fullWidth
              inputProps={{ readOnly: true, 'aria-label': t.apiKeys.keyLabel, style: { fontFamily: 'monospace' } }}
              onFocus={(e) => e.target.select()}
            />
            <Button variant="outlined" startIcon={<CopyIcon />} onClick={onCopy} sx={{ flexShrink: 0 }}>
              {copied ? t.apiKeys.copied : t.apiKeys.copy}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCreated(null)}>
            {t.apiKeys.done}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!toRevoke} onClose={() => !revoking && setToRevoke(null)}>
        <DialogTitle>{t.apiKeys.revokeTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{format(t.apiKeys.revokeBody, { name: toRevoke?.name ?? '' })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToRevoke(null)} disabled={revoking}>
            {t.common.cancel}
          </Button>
          <Button color="error" variant="contained" onClick={onRevoke} disabled={revoking}>
            {t.apiKeys.revoke}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
